import React, { useEffect, useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import NumberFormat from "react-number-format";
import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import PageviewIcon from "@mui/icons-material/Pageview";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import ButtonGroup from "@mui/material/ButtonGroup";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import { projectStatusOptions } from "utils/data";
import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import { Avatar } from "antd";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { useForm, Controller } from "react-hook-form";

import defaultStyleImage from "assets/images/upstyled/default_background.png";

import { filterParser, sortParser } from "utils/helper";
import { projectRequestsSortOptions, accountStatusOptions, accountTypeOptions } from "utils/data";
import { getStyleImages, canResizeImageUrl } from "utils/imageHelper";

import RequestDetailModal from "./components/RequestDetailModal";

const AdminRequests = () => {
  const [maList, setMaList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const user = useContext(AccountContext);
  const [pageSize, setPageSize] = useState(20);
  const [audienceMap, setAudienceMap] = useState({});
  const [detailViewVisible, setDetailViewVisible] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState({ manufacturer: null, projectList: [] });
  const [projectList, setProjectList] = useState([]);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    reset({ sort_by: projectRequestsSortOptions[0], account_status: accountStatusOptions[3] });
  }, []);

  useEffect(() => {
    if (user.id) {
      retrieveProjects();
      retrieveMnufacturers();
    }
  }, [user.id]);

  const retrieveMnufacturers = async () => {
    try {
      //const { data } = await api.project.list({ owner_id: user.id });

      const { data } = await api.users.list({ account_type: "ma" });
      setMaList(data?.users || []);
      setFilteredList(data?.users || []);
    } catch (err) {
      console.log(err);
    }
  };

  const retrieveProjects = async () => {
    const payload = {
      status: "requested",
    };

    try {
      //const { data } = await api.project.list({ owner_id: user.id });

      const { data } = await api.project.listAdmin(payload);
      const allProjects = data?.project || [];
      setProjectList(allProjects);
      let map = {};
      for (let i = 0; i < allProjects.length; i++) {
        let audiences = allProjects[i]?.audiences || [];
        for (let j = 0; j < audiences.length; j++) {
          const maResponse = allProjects[i]?.response ? allProjects[i]?.response[audiences[j]] : {};
          if (map[audiences[j]]) {
            map[audiences[j]].push({ id: allProjects[i].id, response: maResponse });
          } else {
            map[audiences[j]] = [{ id: allProjects[i].id, response: maResponse }];
          }
        }
      }
      setAudienceMap(map);
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterChange = (data) => {
    let newMaList = [...maList];
    if (data?.value) {
      newMaList = newMaList.filter((x) => x.account_status == data.value);
    }

    setFilteredList(newMaList);
  };

  const formatAccountStatus = (type, row) => {
    const option = accountStatusOptions.find((x) => x.value == type);
    if (option) {
      if (option.value == "active") {
        return (
          <SuiBadgeDot
            size="xs"
            color="success"
            badgeContent={<div className="text-gray-800">{option.label} </div>}
          />
        );
      } else if (option.value == "init") {
        return (
          <SuiBadgeDot
            color="warning"
            badgeContent={<div className="text-gray-800">{option.label} </div>}
          />
        );
      } else {
        return (
          <SuiBadgeDot
            color="secondary"
            badgeContent={<div className="text-gray-800">{option.label} </div>}
          />
        );
      }
    } else {
      return type;
    }
  };

  const getProjectTableRow = (item) => {
    const fullName =
      item.first_name || item.last_name ? `${item.first_name} ${item.last_name}` : item.email;

    const store = item.stores && item.stores.length > 0 ? item.stores[0] : {};
    const maRequests = audienceMap[item.id] || [];
    const maResponse = maRequests.filter((x) => x?.response?.message);

    return {
      key: item.email,
      raw_status: item.account_status,
      avatar: store?.metadata?.avatar,
      name: fullName,
      store: store?.name,
      email: item.email,
      permissions: capitalCase(item.role),
      status: item.account_status,
      requests: maRequests,
      requests_num: maRequests.length,
      response_num: maResponse.length,
      action: item,
    };
  };
  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row, value }) => {
        return (
          <div className="flex items-center">
            <SuiAvatar
              src={row?.original?.avatar}
              alt="profile-image"
              variant="circle"
              size="sm"
              shadow="sm"
            />

            <div
              className={`${
                row?.original?.type == "user" ? "text-gray-800" : "text-gray-400"
              } ml-2`}
            >
              {value}
            </div>
          </div>
        );
      },
    },

    {
      Header: "Store/Brand",
      accessor: "store",
      Cell: ({ row, value }) => {
        return (
          <div
            className={`${row?.original?.type == "user" ? "text-gray-800" : "text-gray-400"} ml-2`}
          >
            {value}
          </div>
        );
      },
    },
    {
      Header: "email",
      accessor: "email",
      Cell: ({ row, value }) => {
        return (
          <div
            className={`${row?.original?.type == "user" ? "text-gray-800" : "text-gray-400"} ml-2`}
          >
            {value}
          </div>
        );
      },
    },
    {
      Header: "status",
      accessor: "status",
      Cell: ({ row, value }) => {
        return formatAccountStatus(value);
      },
    },
    {
      Header: "Requests #",
      accessor: "requests_num",
    },
    {
      Header: "Response #",
      accessor: "response_num",
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => (
        <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Tooltip title="Request Details">
            <ManageSearchIcon
              fontSize={"small"}
              color="dark"
              sx={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                let myRequests = row?.original?.requests || [];
                let projectIds = myRequests.map((x) => x.id);
                let myProjects = projectList.filter((x) => projectIds.includes(x.id));
                setSelectedRequests({ manufacturer: value, projectList: myProjects });
                setDetailViewVisible(true);
              }}
            />
          </Tooltip>
        </SuiBox>
      ),
    },
  ];

  const rows = filteredList.map((item) => getProjectTableRow(item));

  return (
    <Card sx={{ p: 3 }}>
      <SuiBox mb={0.5}>
        <SuiTypography variant="h5">Manufacturers</SuiTypography>
      </SuiBox>
      <SuiBox display="flex" justifyContent="space-between" alignItems="flex-end">
        <SuiBox pb={3}>
          <SuiBox
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <SuiBox display="flex" alignItems="center" sx={{ gap: 1.5, flexWrap: "wrap" }}>
              <SuiTypography variant="button" color="dark" fontWeight="regular">
                Filter by:
              </SuiTypography>

              <SuiSelect
                options={accountStatusOptions}
                placeholder="account status"
                backspaceRemovesValue
                isClearable
                onChange={handleFilterChange}
              />
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </SuiBox>
      <DataTable
        table={{ columns, rows: rows }}
        entriesPerPage={{ defaultValue: pageSize }}
        showTotalEntries={true}
        pagination={true}
        noEndBorder
        canSearch
      />
      {detailViewVisible ? (
        <RequestDetailModal
          handleClose={() => setDetailViewVisible(false)}
          //handleOk={(data) => handleDuplicateStyle(data)}
          visible={detailViewVisible}
          detail={selectedRequests}
        />
      ) : null}
    </Card>
  );
};

export default AdminRequests;
